import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, type TableColumnsType } from 'antd';
import { StatusTag } from 'pos-client/components/generic/status-tag/StatusTag.tsx';
import { PosCard } from 'pos-client/components/shared/Display/Card/PosCard.tsx';
import { PosTable } from 'pos-client/components/shared/Display/Table/PosTable.tsx';
import { PosInputSearch } from 'pos-client/components/shared/Form/Search/PosSearchInput.tsx';
import { PosColumn } from 'pos-client/components/shared/Layout/Grid/PosColumn.tsx';
import { PosGrid } from 'pos-client/components/shared/Layout/Grid/PosGrid.tsx';
import { useFilters } from 'pos-client/hooks/useFilters.tsx';
import { useLocations } from 'pos-client/hooks/useLocations.tsx';
import { useTableData } from 'pos-client/hooks/useTableData.ts';
import { PosPage } from 'pos-client/layouts/page/PosPage.tsx';
import { locationTabs } from 'pos-client/tabs/locationTabs.ts';
import { convertISOStringToLocalDate } from 'pos-client/utils/dateHelpers.ts';
import type {
  LocationModel,
  LocationSearchFormModel,
} from 'pos-shared/models/locationSchema.ts';

import { CreateEditLocationModal } from './Modals/CreateEditLocationModal.tsx';

export const LocationsPage = () => {
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const showModal = () => setIsModalOpen(true);
  const hideModal = () => setIsModalOpen(false);

  const { formFilters, setFormFilters, onPageChange } =
    useFilters<LocationSearchFormModel>({
      pageNumber: 1,
      resultsPerPage: 10,
      orderBy: 'Name',
      sortAscending: true,
    });
  const { locationSearchResponse, isLoadingLocations } = useLocations(
    formFilters,
    {},
    true
  );
  const tableData = useTableData(locationSearchResponse?.results, (x) => x.id);

  const columns: TableColumnsType<LocationModel> = [
    {
      title: t('locations.locationName'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('locations.locationCode'),
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: t('common.address.zipCode'),
      dataIndex: 'postalCode',
      key: 'postalCode',
      responsive: ['md'],
      render: (_, record) =>
        record?.address?.postalCode ? record?.address?.postalCode : '-',
    },
    {
      title: t('common.status.status'),
      dataIndex: 'isActive',
      key: 'isActive',
      responsive: ['sm'],
      render: (_, record) => (
        <StatusTag status={record.isActive ? 'active' : 'inactive'} />
      ),
    },
    {
      title: t('common.audit.dateModified'),
      dataIndex: 'lastModified',
      key: 'lastModified',
      responsive: ['lg'],
      render: (_, record) =>
        record.lastModified
          ? convertISOStringToLocalDate(record.lastModified)
          : '-',
    },
    {
      title: '',
      key: 'id',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Link to={`/locations/${record.id}/${locationTabs.summary.key}`}>
          <Button size="small">{t('common.buttons.view')}</Button>
        </Link>
      ),
    },
  ];

  const onSearchSubmit = (value: string) => {
    setFormFilters({
      ...formFilters,
      pageNumber: 1,
      name: value,
    });
  };

  return (
    <PosPage
      header={{
        title: t('locations.locations'),
        description: t('locations.pageDescription'),
        actionComponent: (
          <Button
            type="primary"
            onClick={showModal}
            icon={<FontAwesomeIcon icon={faPlus} />}
          >
            {t('locations.createLocation')}
          </Button>
        ),
      }}
    >
      <PosCard>
        <PosGrid filters>
          <PosColumn span={24} lg={12} xl={8}>
            <PosInputSearch
              placeholder={t('locations.searchLocations')}
              onSearch={onSearchSubmit}
              size="large"
              allowClear
              loading={isLoadingLocations}
            />
          </PosColumn>
        </PosGrid>
        <PosTable
          dataSource={tableData}
          columns={columns}
          isLoading={isLoadingLocations}
          paging={{
            current: locationSearchResponse?.pageNumber || 1,
            total: locationSearchResponse?.totalResults || 0,
            pageSize: formFilters.resultsPerPage,
            onChange: onPageChange,
            showTotal: (total) =>
              t('items.totalItemsCount', {
                count: total,
              }),
            showSizeChanger: true,
          }}
        />
      </PosCard>
      <CreateEditLocationModal isOpen={isModalOpen} hideModal={hideModal} />
    </PosPage>
  );
};
